import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { useLookup } from "@/lib/lookups";
import { errMsg, fmtDate } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, EmptyState, Field } from "@/components/common/States";
import { StatusBadge, Pick, Pager, Table, money, label, SERVICE_TYPES } from "./shared";

const STATUSES = ["open", "draft", "pending_approval", "approved", "in_progress", "completed", "delivery_pending", "delivery_approved", "delivered", "cancelled"];
const today = () => new Date().toISOString().slice(0, 10);

export const NewServiceDialog = ({ open, onOpenChange, vehicleId }) => {
  const { api } = useSession();
  const nav = useNavigate();
  const { items: vehicles } = useLookup("vehicles", open);
  const blank = { vehicle_id: vehicleId || "", service_date: today(), odometer: "", service_type: "periodic", complaint: "", estimate_amount: "", technician_or_garage: "", notes: "" };
  const [v, setV] = useState(blank);
  const [busy, setBusy] = useState(false);
  const s = (k) => (e) => setV((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...v, odometer: v.odometer === "" ? null : Number(v.odometer), estimate_amount: Number(v.estimate_amount || 0) };
      const { data } = await api.post("/service-records", body);
      toast.success(`Service record ${data.number} created`);
      onOpenChange(false); setV(blank);
      nav(`/service-records/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-service-dialog">
        <DialogHeader><DialogTitle>New service record</DialogTitle><DialogDescription>Saved as a draft. Submit it for service approval before work starts.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Vehicle" className="sm:col-span-2">
            <Pick value={v.vehicle_id} onChange={s("vehicle_id")} placeholder="Choose vehicle" className="w-full" options={vehicles.map((x) => ({ value: x.id, label: `${x.registration_number}${x.model ? ` · ${x.make || ""} ${x.model}` : ""}` }))} testId="new-service-vehicle" />
          </Field>
          <Field label="Service date"><Input type="date" value={v.service_date} max={today()} onChange={s("service_date")} data-testid="new-service-date" /></Field>
          <Field label="Odometer (km)"><Input type="number" min="0" value={v.odometer} onChange={s("odometer")} data-testid="new-service-odometer" /></Field>
          <Field label="Service type"><Pick value={v.service_type} onChange={s("service_type")} className="w-full" options={SERVICE_TYPES.map((t) => ({ value: t, label: label(t) }))} testId="new-service-type" /></Field>
          <Field label="Estimated cost (₹)"><Input type="number" min="0" value={v.estimate_amount} onChange={s("estimate_amount")} data-testid="new-service-estimate" /></Field>
          <Field label="Complaint / reason" className="sm:col-span-2"><Textarea rows={2} value={v.complaint} onChange={s("complaint")} data-testid="new-service-complaint" /></Field>
          <Field label="Technician or garage" className="sm:col-span-2"><Input value={v.technician_or_garage} onChange={s("technician_or_garage")} data-testid="new-service-technician" /></Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={v.notes} onChange={s("notes")} data-testid="new-service-notes" /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button disabled={busy || !v.vehicle_id} onClick={save} data-testid="new-service-submit">{busy ? "Saving…" : "Create draft"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ServiceRecords() {
  const { can, branches } = useSession();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: "", status: sp.get("status") || "", branch_id: "", from: "", to: "" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); };
  const params = { page, limit: 25, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
  const { data, loading, error, reload } = useFetch("/service-records", params, can("maintenance.view"));
  if (!can("maintenance.view")) return <NoAccess perm="maintenance.view" />;
  const cols = [
    ["Service no", (r) => <span className="font-mono-g font-medium">{r.number}</span>],
    ["Date", (r) => fmtDate(r.service_date), "whitespace-nowrap"],
    ["Vehicle", (r) => <span className="font-mono-g">{r.registration_number}</span>],
    ["Type", (r) => <span className="capitalize">{label(r.service_type)}</span>],
    ["Complaint", (r) => <span className="line-clamp-1 max-w-[260px]">{r.complaint}</span>],
    ["Total", (r) => money(r.total_cost), "text-right whitespace-nowrap"],
    ["Status", (r) => <StatusBadge s={r.status} testId={`service-status-${r.id}`} />],
  ];
  return (
    <div data-testid="service-records-page">
      <PageHeader title="Service" subtitle="Services and repairs, from approval to delivery."
        actions={can("maintenance.manage") && <Button onClick={() => setOpen(true)} data-testid="new-service-btn"><Plus className="h-4 w-4 mr-1.5" />New service</Button>} />
      <div className="g39-card">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 p-4 border-b bg-secondary/40">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 bg-white" placeholder="Search number, registration, complaint" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="services-search-input" />
          </div>
          <Pick value={f.status} onChange={(v) => set("status", v)} allLabel="All statuses" options={STATUSES.map((x) => ({ value: x, label: x === "open" ? "Open (not delivered)" : label(x) }))} testId="services-status-filter" />
          {branches.length > 1 && <Pick value={f.branch_id} onChange={(v) => set("branch_id", v)} allLabel="All branches" options={branches.map((b) => ({ value: b.id, label: b.name }))} testId="services-branch-filter" />}
          <Input type="date" className="bg-white sm:w-40" value={f.from} onChange={(e) => set("from", e.target.value)} data-testid="services-from" aria-label="From date" />
          <Input type="date" className="bg-white sm:w-40" value={f.to} onChange={(e) => set("to", e.target.value)} data-testid="services-to" aria-label="To date" />
        </div>
        {loading && <LoadingRows rows={5} />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title="No service records" body="Create a service record to start tracking work and costs." testId="services-empty" />}
        {data && data.items.length > 0 && <Table cols={cols} rows={data.items} testId="services-table" onRow={(r) => nav(`/service-records/${r.id}`)} />}
        {data && <Pager page={page} total={data.total} limit={25} onPage={setPage} testId="services-pager" />}
      </div>
      <NewServiceDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
